import {
  AtsCreateForHiringPlanRequisitionDocument,
  type AtsContractTypesEnum,
  type AtsJobPostingSalaryFormatEnum,
  type AtsJobPostingSalaryPeriodEnum,
  type AtsRequisitionReasonsEnum,
  type AtsWorkplaceTypesEnum,
  type OnboardingEntitiesWorkScheduleTimeEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import { OneCreateAction, OneCreationActionResolver } from 'modules/core/lib/oneCreateActions/types'

interface CreateRequisitionArgs {
  jobPostingTitle: string
  teamId: string
  workplaceId: string
  workplaceType: AtsWorkplaceTypesEnum
  reason: AtsRequisitionReasonsEnum
  deadline: string
  salaryCurrency: string
  salaryFromAmountInCents: number
  salaryType: AtsJobPostingSalaryFormatEnum
  salaryToAmountInCents?: number
  salaryPeriod?: AtsJobPostingSalaryPeriodEnum
  contractType?: AtsContractTypesEnum
  scheduleType?: OnboardingEntitiesWorkScheduleTimeEnum
  numberOfPositions?: number
  startDate?: string
  note?: string
  jobCatalogLevelUuid?: string
  jobCatalogRoleUuid?: string
}

const useCreateRequisition: OneCreationActionResolver = () => {
  const { company, employee, access } = useCurrentSession()
  const { navigation } = useNavigation()

  const [createRequisition, { loading }] = useMutation(AtsCreateForHiringPlanRequisitionDocument, {
    extractorFn: (data) => data.atsMutations.createForHiringPlanAtsRequisition,
    errorPolicy: 'all',
  })

  const createRequisitionHandler = async (args: CreateRequisitionArgs) => {
    if (!company || !employee || !access) {
      return
    }

    const result = await createRequisition({
      variables: {
        authorId: access.id,
        companyId: company.id,
        lineManagerId: employee.id,
        jobPostingTitle: args.jobPostingTitle,
        teamId: args.teamId,
        workplaceId: args.workplaceId,
        workplaceType: args.workplaceType,
        reason: args.reason,
        deadline: args.deadline,
        salaryCurrency: args.salaryCurrency,
        salaryFromAmountInCents: args.salaryFromAmountInCents,
        salaryType: args.salaryType,
        salaryToAmountInCents: args.salaryToAmountInCents,
        salaryPeriod: args.salaryPeriod,
        contractType: args.contractType,
        scheduleType: args.scheduleType,
        numberOfPositions: args.numberOfPositions,
        startDate: args.startDate,
        note: args.note,
        jobCatalogLevelUuid: args.jobCatalogLevelUuid,
        jobCatalogRoleUuid: args.jobCatalogRoleUuid,
      },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success && handled.data.requisition?.id) {
      return {
        to: navigation.ats.hiringPlan.requisitions.requisitionDetail.fullPath({
          requisition_id: handled.data.requisition.id,
        }),
        data: { ...args },
      }
    }

    return handled
  }

  return {
    resolver: createRequisitionHandler,
    loading,
  }
}

export const CreateRequisitionAction: OneCreateAction = {
  actionResolver: useCreateRequisition,
  policies: ['Ats_requisitionsManage'],
  module: 'recruitment',
}
